/**
 * LocalAgentChat — agent de la fiche exécuté en local (SmartLawyer Desktop + Ollama).
 * La décision consultée sert de contexte ; un dossier client local peut être joint.
 * Rien ne quitte le poste, sauf les questions de recherche anonymisées.
 */
import { desktopBridge } from '@/AppMode';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

interface Message {
  role: 'user' | 'assistant';
  content: string;
  steps: string[];
  error?: string;
}

interface Dossier {
  id: string;
  name: string;
}

const TOOL_LABELS: Record<string, string> = {
  rechercher_jurisprudence: 'Jurisprudence SmartLawyer',
  rechercher_articles: 'Codes SmartLawyer',
  rechercher_dossier: 'Dossier client (local)'
};

export function LocalAgentChat({
  context,
  initialPrompt
}: {
  context: string;
  initialPrompt?: string;
}) {
  const { t } = useTranslation();
  const sl = desktopBridge();
  const [ready, setReady] = useState<boolean | null>(null);
  const [model, setModel] = useState('');
  const [dossiers, setDossiers] = useState<Dossier[]>([]);
  const [dossierId, setDossierId] = useState('');
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState('');
  const [requestId, setRequestId] = useState<string | null>(null);
  const bottomRef = useRef<HTMLDivElement>(null);
  const started = useRef(false);

  useEffect(() => {
    if (!sl) return;
    Promise.all([sl.ollama.status(), sl.settings.get(), sl.dossiers.list()])
      .then(
        async ([status, settings, list]: [
          { running: boolean },
          { chatModel: string },
          Dossier[]
        ]) => {
          setModel(settings.chatModel);
          setDossiers(list);
          const models: { name: string }[] = status.running
            ? await sl.ollama.models()
            : [];
          setReady(
            models.some(
              (m) =>
                m.name === settings.chatModel ||
                m.name === `${settings.chatModel}:latest`
            )
          );
        }
      )
      .catch(() => setReady(false));
  }, [sl]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  useEffect(() => {
    if (!sl) return;
    // Le désabonnement vient du pont Electron (contextBridge) : on l'enveloppe
    // dans une vraie fonction de nettoyage React.
    const off = sl.chat.onEvent((ev: any) => {
      if (ev.requestId !== requestId) return;
      setMessages((prev) => {
        const next = [...prev];
        const last = { ...next[next.length - 1] };
        if (ev.type === 'token') last.content += ev.text;
        if (ev.type === 'tool') {
          last.content = '';
          last.steps = [...last.steps, `🔎 ${TOOL_LABELS[ev.name] ?? ev.name}`];
        }
        if (ev.type === 'tool_result')
          last.steps = [
            ...last.steps,
            `   ↳ ${
              ev.error ? '⚠️ ' + ev.error : `${ev.count ?? 0} résultat(s)`
            }`
          ];
        if (ev.type === 'error') last.error = ev.message;
        next[next.length - 1] = last;
        return next;
      });
      if (ev.type === 'done' || ev.type === 'error') setRequestId(null);
    });
    return () => {
      if (typeof off === 'function') off();
    };
  }, [sl, requestId]);

  const send = async (q: string) => {
    const question = q.trim();
    if (!sl || !question || requestId) return;
    const id = crypto.randomUUID();
    const history = [
      ...messages,
      { role: 'user' as const, content: question }
    ].map(({ role, content }) => ({ role, content }));
    setMessages((m) => [
      ...m,
      { role: 'user', content: question, steps: [] },
      { role: 'assistant', content: '', steps: [] }
    ]);
    setInput('');
    setRequestId(id);
    await sl.chat.ask({
      requestId: id,
      history,
      context,
      dossierId: dossierId || undefined
    });
  };

  useEffect(() => {
    if (ready && initialPrompt && !started.current) {
      started.current = true;
      send(initialPrompt);
    }
    // eslint-disable-next-line
  }, [ready, initialPrompt]);

  if (!sl) return null;
  if (ready === false) {
    return (
      <div className="p-6 text-sm text-muted-foreground">
        {t('desktop.home.local_unavailable')}{' '}
        <button
          className="underline"
          onClick={() => sl.app.openLocal('modeles')}
        >
          →
        </button>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      <div className="flex items-center gap-2 px-5 py-2 border-b text-xs text-muted-foreground">
        <span>🔒 {model}</span>
        <select
          value={dossierId}
          onChange={(e) => setDossierId(e.target.value)}
          className="ml-auto text-xs border border-border rounded-md px-2 py-1 bg-background"
        >
          <option value="">— sans dossier client —</option>
          {dossiers.map((d) => (
            <option key={d.id} value={d.id}>
              📁 {d.name}
            </option>
          ))}
        </select>
      </div>
      <div className="flex-1 overflow-y-auto px-5 py-4 space-y-3">
        {messages.map((m, i) => (
          <div
            key={i}
            className={
              m.role === 'user'
                ? 'ml-auto max-w-[80%] bg-muted rounded-2xl px-4 py-2 text-sm whitespace-pre-wrap'
                : 'max-w-[95%] text-sm'
            }
          >
            {m.steps.length > 0 && (
              <pre className="text-[11px] text-muted-foreground whitespace-pre-wrap mb-1 font-mono">
                {m.steps.join('\n')}
              </pre>
            )}
            {m.content && (
              <div className="whitespace-pre-wrap leading-relaxed">
                {m.content}
              </div>
            )}
            {m.role === 'assistant' &&
              !m.content &&
              !m.error &&
              requestId &&
              i === messages.length - 1 && (
                <div className="text-muted-foreground animate-pulse">…</div>
              )}
            {m.error && (
              <div className="text-destructive text-xs">{m.error}</div>
            )}
          </div>
        ))}
        <div ref={bottomRef} />
      </div>
      <form
        className="flex gap-2 px-5 py-3 border-t"
        onSubmit={(e) => {
          e.preventDefault();
          send(input);
        }}
      >
        <input
          value={input}
          onChange={(e) => setInput(e.target.value)}
          disabled={!ready}
          placeholder="Question sur cette décision…"
          className="flex-1 text-sm border border-border rounded-xl px-3 py-2 bg-background"
        />
        {requestId ? (
          <button
            type="button"
            onClick={() => sl.chat.abort(requestId)}
            className="text-sm px-3 rounded-xl border border-border"
          >
            ■
          </button>
        ) : (
          <button
            type="submit"
            disabled={!ready || !input.trim()}
            className="text-sm px-4 rounded-xl bg-primary text-primary-foreground disabled:opacity-50"
          >
            ↑
          </button>
        )}
      </form>
    </div>
  );
}
